import { z } from "zod"

import {
  CommunicationStudioClasses,
  CommunicationStudioCssVariables,
  CUSTOMISATION_CATEGORIES,
} from "@tfg/types/constants/communication-studio.constant"
import { ZodFormObjectTypeDescription } from "@tfg/types/constants/zodFromInputTypes"
import {
  AppHeaderClassSchema,
  ContentBlocksClassSchema,
  LogoClassSchema,
  MainContainerClassSchema,
  MainHeaderClassSchema,
  ActivityClassSchema,
  CountryClassSchema,
  PackagingInfoHyperLinkClassSchema,
  PackagingInfoHeaderClassSchema,
  SearchEngineHeaderClassSchema,
  LastUpdateClassSchema,
  ProductReferenceClassSchema,
  ProductNameClassSchema,
  ProductTitleClassSchema,
  RecycledPackagingKeyInfoClassSchema,
  SubTextHeaderClassSchema,
  GenericPassportTitleClassSchema,
  ProdcutAttributesPrimaryInformationClassSchema,
  ProdcutAttributesSecondaryInformationClassSchema,
} from "@tfg/types/schemas/communication-studio/classes"
import {
  CSSColorSchema,
  cssLengthSchema,
} from "@tfg/types/schemas/communication-studio/cssStylesSchema"
import {
  textColorCssVariable,
  titleColorCSSVariable,
  contentVerticalSpacingCSSVariable,
  paddingBlockCSSVariable,
  textFontSizeCSSVariable,
  headerFontSizeCSSVariable,
  headerFontWeightCSSVariable,
  textFontWeightCSSVariable,
  paddingInlineCSSVariable,
  searchEngineTextColorCSSVariable,
  productCardBadgeBackgroundColorCSSVariable,
  productCardBadgeTextColorCSSVariable,
  headerTextDecorationCSSVariable,
  headerTextTransformCSSVariable,
  headerLineHeightCSSVariable,
  ecoscoreAlignCSSVariable,
  headerFontFamilyCSSVariable,
  logoAlignCSSVariable,
  productAttributesSpacingCSSVariable,
} from "@tfg/types/schemas/communication-studio/cssVariables"

export const GlobalCategoryStylingParametersSchema = z
  .object({
    height: cssLengthSchema,
    width: cssLengthSchema,
    backgroundColor: CSSColorSchema,
    appHeader: AppHeaderClassSchema,
    fontFamily: z.string(),
    letterSpacing: cssLengthSchema,
    lineHeight: cssLengthSchema,
    paddingInline: paddingInlineCSSVariable,
    paddingBlock: paddingBlockCSSVariable,
    titleColor: titleColorCSSVariable,
    headerFontSize: headerFontSizeCSSVariable,
    headerTextTransform: headerTextTransformCSSVariable,
    headerFontWeight: headerFontWeightCSSVariable,
    headerTextDecoration: headerTextDecorationCSSVariable,
    headerLineHeight: headerLineHeightCSSVariable,
    headerFontFamily: headerFontFamilyCSSVariable,
    contentVerticalSpacing: contentVerticalSpacingCSSVariable,
    textFontSize: textFontSizeCSSVariable,
    textColor: textColorCssVariable,
    textFontWeight: textFontWeightCSSVariable,
    [CommunicationStudioClasses.MAIN_CONTAINER]: MainContainerClassSchema,
    [CommunicationStudioClasses.CONTENT_BLOCKS]: ContentBlocksClassSchema,
  })
  .partial()

export const MainHeaderCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.MAIN_HEADER]: MainHeaderClassSchema,
    [CommunicationStudioClasses.PRODUCT_NAME]: ProductNameClassSchema,
    [CommunicationStudioClasses.PRODUCT_REFERENCE]: ProductReferenceClassSchema,
    [CommunicationStudioClasses.PRODUCT_TITLE]: ProductTitleClassSchema,
    [CommunicationStudioClasses.SUB_TEXT_HEADER]: SubTextHeaderClassSchema,
  })
  .partial()

export const ActivitiesCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.ACTIVITY]: ActivityClassSchema,
    [CommunicationStudioClasses.COUNTRY]: CountryClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const PackagingInfoCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.PACKAGING_INFO_HEADER]:
      PackagingInfoHeaderClassSchema,
    [CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO]:
      RecycledPackagingKeyInfoClassSchema,
    [CommunicationStudioClasses.PACKAGING_INFO_HYPER_LINK]:
      PackagingInfoHyperLinkClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const SearchEngineCategoryStylingParametersSchema = z
  .object({
    backgroundColor: CSSColorSchema,
    productCardBadgeBackgroundColor: productCardBadgeBackgroundColorCSSVariable,
    productCardBadgeTextColor: productCardBadgeTextColorCSSVariable,
    searchEngineTextColor: searchEngineTextColorCSSVariable,
    fontFamily: z.string(),
    [CommunicationStudioClasses.SEARCH_ENGINE_HEADER]:
      SearchEngineHeaderClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const FooterCategoryStylingParametersSchema = z
  .object({
    marginBottom: cssLengthSchema,
    marginTop: cssLengthSchema,
    [CommunicationStudioClasses.LAST_UPDATE]: LastUpdateClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const EcoscoreCategoryStylingParametersSchema = z
  .object({
    ecoscoreAlign: ecoscoreAlignCSSVariable.partial().optional(),
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const LogoCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.LOGO]: LogoClassSchema.optional(),
    logoAlign: logoAlignCSSVariable,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProductAttributesCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_PRIMARY_INFORMATION]:
      ProdcutAttributesPrimaryInformationClassSchema,
    [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_SECONDARY_INFORMATION]:
      ProdcutAttributesSecondaryInformationClassSchema,
    [CommunicationStudioCssVariables.PRODUCT_ATTRIBUTES_SPACING]:
      productAttributesSpacingCSSVariable,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const GenericPassportTitleCategoryStylingParametersSchema = z
  .object({
    genericPassportTitle: GenericPassportTitleClassSchema.optional(),
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const CategoryStylingParametersMapper = {
  [CUSTOMISATION_CATEGORIES.GLOBAL]:
    GlobalCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.MAIN_HEADER]:
    MainHeaderCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]:
    ActivitiesCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.PACKAGING_INFO]:
    PackagingInfoCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.FOOTER]: FooterCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.SEARCH_ENGINE]:
    SearchEngineCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.ECOSCORE]:
    EcoscoreCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.LOGO]:
    LogoCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.PRODUCT_ATTRIBUTES]:
    ProductAttributesCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE]:
    GenericPassportTitleCategoryStylingParametersSchema.partial(),
}

export const StylingParametersSchema = z.object({
  [CUSTOMISATION_CATEGORIES.GLOBAL]:
    GlobalCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.MAIN_HEADER]:
    MainHeaderCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]:
    ActivitiesCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.PACKAGING_INFO]:
    PackagingInfoCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.FOOTER]: FooterCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.SEARCH_ENGINE]:
    SearchEngineCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.ECOSCORE]:
    EcoscoreCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.LOGO]:
    LogoCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.PRODUCT_ATTRIBUTES]:
    ProductAttributesCategoryStylingParametersSchema.partial(),
  [CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE]:
    GenericPassportTitleCategoryStylingParametersSchema.partial(),
})
